/* ========== LINE CHARTS ==========
 * Subtypes:
 *  - Line (drawLineChart)
 *  - Line with Markers (drawLineChart, hasMarker=true)
 *  - Stacked Line (drawStackedLineChart, isPercent=false)
 *  - 100% Stacked Line (drawStackedLineChart, isPercent=true)
 *  - 3-D Line (draw3DLineChart — oblique projection like Excel)
 *  - Area (drawLineChart, fillArea=true) — used by area.js for non-stacked case
 *
 * Point placement: Excel line charts with a multi-level category axis use
 * "between ticks" placement — each point sits at the CENTER of its
 * category slot. 3-D line always uses slot centers.
 * ================================== */

import {
  COLORS,
  drawAxes,
  drawGridlines,
  drawLegend,
  drawXAxisLabels,
  getMinMaxRange,
  niceAxis,
  drawValueLabel,
  shadeColor,
} from './utils';

/** True when categories are placed between tick marks (slot-centered). */
function isCatBetween(chartObj) {
  var ax = chartObj && chartObj.valAxis;
  return !!(ax && ax.crossBetween === 'between');
}

/** Build an x-position function: slot-centered for multi-level axes. */
function makePosX(x, width, numCategories, centered) {
  if (centered) {
    var slotW = width / Math.max(1, numCategories);
    return function (i) { return x + (i + 0.5) * slotW; };
  }
  var spacing = width / Math.max(1, numCategories - 1);
  return function (i) { return x + i * spacing; };
}

/** Truncate a label to maxW px in the current font. */
function fit(ctx, label, maxW) {
  if (ctx.measureText(label).width <= maxW) return label;
  var l = label;
  while (l.length > 1 && ctx.measureText(l + '…').width > maxW) l = l.slice(0, -1);
  return l + '…';
}

/* ========== 3-D LINE (Excel oblique projection) ==========
 * Matches Excel's default 3-D rotation visually:
 *  - value gridlines slant UP to the right and extend past the plot
 *  - data ribbons live on the same slanted plane
 *  - the floor / category band slants DOWN to the right, so each label
 *    column steps lower than the previous one
 *  - multi-level axes: leaf level (e.g. weekday) is drawn VERTICALLY,
 *    parent levels (year, month) horizontally beneath it
 *  - single series: the series name sits at the right along the depth
 *    axis ("Sales"), no bottom legend
 * ========================================================== */
export function draw3DLineChart(ctx, x, y, width, height, chartObj) {
  var seriesArr = chartObj && chartObj.series ? chartObj.series : [];
  if (seriesArr.length === 0) return;
  var mmr = getMinMaxRange(seriesArr, chartObj, height);
  var numCategories = Math.max.apply(null, seriesArr.map(function (s) { return s.data.length; }));
  if (numCategories === 0) return;

  var catLevels = chartObj.catLevels || null;
  var cats = chartObj.categories || [];
  var depth = Math.min(28, Math.max(12, width * 0.07));
  var upSlope = 0.08;    // back-wall gridlines ascend to the right
  var downSlope = 0.08;  // floor / category axis descends to the right
  var slotW = width / Math.max(1, numCategories);

  // Excel's 3-D perspective: the VALUE axis is a short block at the top
  // left (its height H), and the floor edge descends from (x, y+H) on the
  // left down to (x+width, y+height) on the right. Data points are
  // anchored to the FLOOR and lifted by their value — so the whole ribbon
  // flows down-right with the perspective instead of floating at the top.
  var H = Math.max(40, height - width * downSlope);
  var floorY = function (px) { return y + H + (px - x) * downSlope; };

  // ── Value gridlines: ascend to the right, extended past the plot ───────
  var gridExt = width + depth + 16;
  ctx.strokeStyle = '#E0E0E0';
  ctx.lineWidth = 1;
  var nGrid = mmr.numGridLines || 5;
  for (var gi = 0; gi <= nGrid; gi++) {
    var gy = y + H - (gi / nGrid) * H;
    ctx.beginPath();
    ctx.moveTo(x, gy);
    ctx.lineTo(x + gridExt, gy - gridExt * upSlope);
    ctx.stroke();
    var val = mmr.minValue + (gi / nGrid) * mmr.range;
    ctx.fillStyle = '#888';
    ctx.font = '9px Arial';
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';
    ctx.fillText(mmr.label(val), x - 4, gy);
  }

  // ── Ribbons back-to-front, anchored to the descending floor ────────────
  var n = seriesArr.length;
  var dslot = depth / Math.max(1, n);
  var ribbonD = Math.max(5, Math.min(10, dslot * 0.8));
  for (var si = n - 1; si >= 0; si--) {
    var s = seriesArr[si];
    var sdata = s.data;
    if (!sdata || sdata.length === 0) continue;
    var col = s.color || COLORS[si % COLORS.length];
    var off = dslot * (n - 1 - si);

    var pts = [];
    for (var pi = 0; pi < sdata.length; pi++) {
      var px = x + (pi + 0.5) * slotW + off;
      var norm = (sdata[pi] - mmr.minValue) / mmr.range;
      var py = floorY(px) - norm * H - off;
      pts.push({ px: px, py: py });
    }

    for (var qi = 0; qi < pts.length - 1; qi++) {
      ctx.fillStyle = col;
      ctx.beginPath();
      ctx.moveTo(pts[qi].px, pts[qi].py);
      ctx.lineTo(pts[qi + 1].px, pts[qi + 1].py);
      ctx.lineTo(pts[qi + 1].px + ribbonD, pts[qi + 1].py - ribbonD);
      ctx.lineTo(pts[qi].px + ribbonD, pts[qi].py - ribbonD);
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = shadeColor(col, -28);
      ctx.lineWidth = 1;
      ctx.stroke();
    }
    ctx.strokeStyle = shadeColor(col, -15);
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    for (var li2 = 0; li2 < pts.length; li2++) {
      if (li2 === 0) ctx.moveTo(pts[li2].px, pts[li2].py);
      else ctx.lineTo(pts[li2].px, pts[li2].py);
    }
    ctx.stroke();
  }

  // ── Floor / category label band: attaches to the floor edge ────────────
  var bandTop = floorY;

  ctx.font = '9px Arial';
  var leafLabels = catLevels ? catLevels[0] : cats;
  var maxLeafW = 0;
  for (var mi = 0; mi < numCategories; mi++) {
    var ll = leafLabels && leafLabels[mi] != null && leafLabels[mi] !== '' ? '' + leafLabels[mi] : '' + (mi + 1);
    var lw = ctx.measureText(ll).width;
    if (lw > maxLeafW) maxLeafW = lw;
  }
  var vertical = !!(catLevels && catLevels.length > 1); // Excel: vertical leaf labels in 3-D
  var leafBandH = vertical ? Math.min(80, maxLeafW + 12) : 17;
  var extraLevels = catLevels ? catLevels.length - 1 : 0;
  var bandH = leafBandH + extraLevels * 16 + 6;

  // Band edges + separators (follow the descending floor)
  ctx.strokeStyle = '#D9D9D9';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(x, bandTop(x));
  ctx.lineTo(x + width, bandTop(x + width));
  ctx.stroke();
  for (var k = 0; k <= numCategories; k++) {
    var sx = x + k * slotW;
    ctx.beginPath();
    ctx.moveTo(sx, bandTop(sx));
    ctx.lineTo(sx, bandTop(sx) + bandH);
    ctx.stroke();
  }

  // Column labels — each column steps lower (3-D floor)
  for (var ci = 0; ci < numCategories; ci++) {
    var ccx = x + (ci + 0.5) * slotW;
    var cTop = bandTop(ccx);
    var leaf = leafLabels && leafLabels[ci] != null && leafLabels[ci] !== '' ? '' + leafLabels[ci] : '' + (ci + 1);

    ctx.fillStyle = '#666';
    ctx.font = '9px Arial';
    if (vertical) {
      // Vertical (rotated 90°, reading bottom-to-top), like the original
      ctx.save();
      ctx.translate(ccx, cTop + leafBandH / 2 + 2);
      ctx.rotate(-Math.PI / 2);
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(fit(ctx, leaf, leafBandH - 4), 0, 0);
      ctx.restore();
    } else {
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      ctx.fillText(fit(ctx, leaf, slotW - 4), ccx, cTop + 5);
    }

    // Parent levels: horizontal rows beneath the leaf band
    if (catLevels) {
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      for (var lv = 1; lv < catLevels.length; lv++) {
        var plbl = catLevels[lv][ci] != null ? '' + catLevels[lv][ci] : '';
        // Skip consecutive repeats (Excel writes spanning labels once)
        if (!plbl || (ci > 0 && ('' + catLevels[lv][ci - 1]) === plbl)) continue;
        ctx.fillStyle = '#666';
        ctx.fillText(fit(ctx, plbl, slotW - 4), ccx, cTop + leafBandH + (lv - 1) * 16 + 8);
      }
    }
  }

  // ── Axes: left value axis (short block) + descending floor edge ────────
  ctx.strokeStyle = '#999';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(x, y);
  ctx.lineTo(x, y + H);
  ctx.lineTo(x + width, y + H + width * downSlope);
  ctx.stroke();

  // ── Series name on the depth axis (single series), legend otherwise ────
  if (n === 1 && seriesArr[0].name) {
    ctx.fillStyle = '#444';
    ctx.font = '10px Calibri, Arial';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText(seriesArr[0].name, x + width + 8, bandTop(x + width) + 10);
  } else if (n > 1) {
    drawLegend(ctx, x, y, width, height, seriesArr, {
      lineStyle: true,
      labelBand: bandH + width * downSlope + 4,
    });
  }
}

export function drawLineChart(ctx, x, y, width, height, chartObj, fillArea, is3D, hasMarker) {
  var seriesArr = chartObj && chartObj.series ? chartObj.series : [];
  if (seriesArr.length === 0) return;

  // 3-D Line has its own projection + label band.
  if (is3D && !fillArea) {
    draw3DLineChart(ctx, x, y, width, height, chartObj);
    return;
  }

  var depth = is3D ? Math.min(28, Math.max(12, width * 0.07)) : 0;
  var mmr = getMinMaxRange(seriesArr, chartObj, height);
  var numCategories = Math.max.apply(null, seriesArr.map(function (s) { return s.data.length; }));
  var catLevels = chartObj.catLevels || null;
  // Excel draws line points in the MIDDLE of each category slot unless the
  // value axis crosses "midCat" (points on the tick marks).
  var centered = !!(catLevels && catLevels.length > 1) || isCatBetween(chartObj);
  var posX = makePosX(x, width, numCategories, centered);

  drawGridlines(ctx, x, y, width, height, mmr.minValue, mmr.range, mmr.numGridLines, mmr.label);

  seriesArr.forEach(function (series, sIdx) {
    var data = series.data;
    if (!data || data.length === 0) return;
    var color = series.color || COLORS[sIdx % COLORS.length];

    if (fillArea) {
      // 3-D depth shadow for area charts
      if (is3D) {
        ctx.globalAlpha = 0.15;
        ctx.fillStyle = shadeColor(color, -20);
        ctx.beginPath();
        data.forEach(function (value, index) {
          var px = posX(index) + depth;
          var py = y + height - ((value - mmr.minValue) / mmr.range) * height - depth;
          if (index === 0) ctx.moveTo(px, py);
          else ctx.lineTo(px, py);
        });
        ctx.lineTo(posX(data.length - 1) + depth, y + height - depth);
        ctx.lineTo(posX(0) + depth, y + height - depth);
        ctx.closePath();
        ctx.fill();
        ctx.globalAlpha = 1;
      }
      ctx.globalAlpha = 0.35;
      ctx.fillStyle = color;
      ctx.beginPath();
      data.forEach(function (value, index) {
        var px = posX(index);
        var py = y + height - ((value - mmr.minValue) / mmr.range) * height;
        if (index === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      });
      ctx.lineTo(posX(data.length - 1), y + height);
      ctx.lineTo(posX(0), y + height);
      ctx.closePath();
      ctx.fill();
      ctx.globalAlpha = 1;
    }

    ctx.strokeStyle = color;
    ctx.lineWidth = 2;
    ctx.beginPath();
    data.forEach(function (value, index) {
      var px = posX(index);
      var py = y + height - ((value - mmr.minValue) / mmr.range) * height;
      if (index === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    });
    ctx.stroke();

    // Value data labels when the chart authored them.
    data.forEach(function (value, index) {
      drawValueLabel(ctx, chartObj, value, posX(index), y + height - ((value - mmr.minValue) / mmr.range) * height - 5);
    });

    // Markers ONLY when the file authored them ("Line with Markers")
    if (hasMarker) {
      data.forEach(function (value, index) {
        var px = posX(index);
        var py = y + height - ((value - mmr.minValue) / mmr.range) * height;
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.arc(px, py, 4, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 1.2;
        ctx.stroke();
      });
    }
  });

  var cats = chartObj.categories || [];
  var catSpacing = centered
    ? width / Math.max(1, numCategories)
    : width / Math.max(1, numCategories - 1);
  var band = drawXAxisLabels(ctx, x, y + height, cats, numCategories, catSpacing, centered, catLevels);

  drawAxes(ctx, x, y, width, height);
  drawLegend(ctx, x, y, width, height, seriesArr, { lineStyle: true, labelBand: band });
}

export function drawStackedLineChart(ctx, x, y, width, height, chartObj, isPercent, hasMarker) {
  var seriesArr = chartObj && chartObj.series ? chartObj.series : [];
  if (seriesArr.length === 0) return;
  var numCategories = Math.max.apply(null, seriesArr.map(function (s) { return s.data.length; }));
  if (numCategories === 0) return;

  var catLevels = chartObj.catLevels || null;
  // Excel draws line points in the MIDDLE of each category slot unless the
  // value axis crosses "midCat" (points on the tick marks).
  var centered = !!(catLevels && catLevels.length > 1) || isCatBetween(chartObj);
  var posX = makePosX(x, width, numCategories, centered);

  var totals = new Array(numCategories).fill(0);
  for (var c = 0; c < numCategories; c++) {
    seriesArr.forEach(function (s) { totals[c] += s.data[c] || 0; });
  }
  var maxTotal = isPercent ? 1 : Math.max.apply(null, totals.concat([1]));
  // Excel-like value axis (nice major unit, authored min/max/format).
  var ax = niceAxis(0, maxTotal, { axis: chartObj.valAxis, lengthPx: height, isPercent: isPercent, forceZero: true });
  var minValue = ax.minValue;
  var range = ax.range;
  drawGridlines(ctx, x, y, width, height, minValue, range, ax.numGridLines, ax.label);

  var runningSums = new Array(numCategories).fill(0);

  seriesArr.forEach(function (series, sIdx) {
    var color = series.color || COLORS[sIdx % COLORS.length];

    ctx.strokeStyle = color;
    ctx.lineWidth = 2;
    ctx.beginPath();
    for (var ci = 0; ci < numCategories; ci++) {
      var v = series.data[ci] || 0;
      var displayVal = isPercent ? (v / (totals[ci] || 1)) : v;
      runningSums[ci] += displayVal;
      var px = posX(ci);
      var py = y + height - (runningSums[ci] / range) * height;
      if (ci === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    }
    ctx.stroke();

    if (hasMarker) {
      for (var ci3 = 0; ci3 < numCategories; ci3++) {
        var px2 = posX(ci3);
        var py2 = y + height - (runningSums[ci3] / range) * height;
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.arc(px2, py2, 4, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 1.2;
        ctx.stroke();
      }
    }
  });

  var cats = chartObj.categories || [];
  var catSpacing = centered
    ? width / Math.max(1, numCategories)
    : width / Math.max(1, numCategories - 1);
  var band = drawXAxisLabels(ctx, x, y + height, cats, numCategories, catSpacing, centered, catLevels);

  drawAxes(ctx, x, y, width, height);
  drawLegend(ctx, x, y, width, height, seriesArr, { lineStyle: true, labelBand: band });
}
